import React, { useState, useEffect, useRef } from "react";
import { ref, onValue } from "firebase/database";
import { database as realtimeDB } from "../FirebaseConfig";
import Lottie from "lottie-react";
import { motion, AnimatePresence } from "framer-motion";
import noReviewsAnimation from "./no-reviews.json"; // Ensure this file exists

const Reviews = ({ id }) => {
  const [reviews, setReviews] = useState([]);
  const [overallRating, setOverallRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const isMobile = window.innerWidth < 768; // Check if mobile

  useEffect(() => {
    if (!id) return;

    const reviewsRef = ref(realtimeDB, `reviews/${id}`);

    const unsubscribe = onValue(reviewsRef, (snapshot) => {
      if (snapshot.exists()) {
        let allReviews = Object.values(snapshot.val() || {});
        setReviewCount(allReviews.length);

        if (allReviews.length > 0) {
          const totalRating = allReviews.reduce((acc, rev) => acc + rev.rating, 0);
          setOverallRating((totalRating / allReviews.length).toFixed(1));
        }

        let shuffled = allReviews.sort(() => 0.5 - Math.random());
        setReviews([...shuffled, ...shuffled]); // Duplicate for infinite scrolling
      } else {
        setReviews([]);
        setReviewCount(0);
        setOverallRating(0);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [id]);

  return (
    <div className="mt-16 bg-white p-6 md:p-8 rounded-xl shadow-lg border border-gray-200">
      {/* Overall Rating Summary */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 md:mb-8">
        <div className="text-center md:text-left">
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-800">Customer Reviews</h2>
          {reviewCount > 0 ? (
            <p className="text-gray-600 mt-1 md:mt-2 text-md md:text-lg">
              ⭐ <span className="text-lg md:text-xl font-bold">{overallRating}</span> out of 5 stars ({reviewCount} reviews)
            </p>
          ) : (
            <p className="text-gray-500 mt-1 md:mt-2 text-sm md:text-md">No ratings yet. Be the first to review! 🌟</p>
          )}
        </div>
      </div>

      {/* No Reviews Message */}
      {loading ? (
        <div className="flex justify-center items-center h-40">
          <p className="text-gray-500 text-lg">Loading reviews...</p>
        </div>
      ) : reviewCount === 0 ? (
        <div className="flex flex-col justify-center items-center">
          <Lottie animationData={noReviewsAnimation} loop autoPlay style={{ width: "150px", md: "200px" }} />
          <p className="text-gray-600 mt-4 text-md md:text-lg italic">
            No reviews yet! Be the first to share your experience. 😊
          </p>
        </div>
      ) : (
        // Framer Motion Scroll Section with AnimatePresence
        <motion.div
          ref={scrollRef}
          className={`relative overflow-hidden ${isMobile ? "h-[100px]" : "w-full"}`}
          whileHover={{ pause: true }} // Pause animation on hover in desktop view
        >
          <AnimatePresence>
            <motion.div
              className={`flex ${isMobile ? "flex-col space-y-4" : "flex-row space-x-6"} p-4`}
              animate={{ x: isMobile ? 0 : [-100, -200, -300, -400, -500, -600, -700, -800, -100], y: isMobile ? [-100, -200, -300, -400, -500] : 0 }}
              transition={{
                repeat: Infinity,
                repeatType: "mirror",
                duration: 20, // Adjust speed
                ease: "linear",
              }}
            >
              {reviews.map((review, index) => (
                <motion.div
                  key={index}
                  className="bg-white/80 backdrop-blur-lg p-6 min-w-[250px] sm:min-w-[300px] md:min-w-[350px] lg:min-w-[400px] rounded-xl shadow-xl hover:shadow-2xl transition-transform transform hover:-translate-y-2 duration-300 border border-gray-300"
                  whileHover={{ scale: 1.05 }} // Slight scale effect on hover
                  initial={{ opacity: 0, y: isMobile ? 30 : 0, x: isMobile ? 0 : 30 }}
                  animate={{ opacity: 1, y: 0, x: 0 }}
                  exit={{ opacity: 0, y: isMobile ? -30 : 0, x: isMobile ? 0 : -30 }}
                  transition={{ duration: 0.5 }}
                >
                  {/* Name Tag */}
                  <div className="mb-2 px-3 py-1 inline-block bg-gradient-to-r from-orange-500 to-yellow-400 text-white font-semibold text-sm rounded-full shadow-md">
                    {review.name}
                  </div>

                  {/* Rating Stars */}
                  <div className="flex mt-2 space-x-1">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <span key={i} className="text-yellow-400 text-xl">★</span>
                    ))}
                    {Array.from({ length: 5 - review.rating }).map((_, i) => (
                      <span key={i} className="text-gray-300 text-xl">★</span>
                    ))}
                  </div>

                  {/* Review Content */}
                  <p className="mt-2 text-gray-700 leading-relaxed italic text-sm md:text-md">
                    “{review.review}”
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
};

export default Reviews;
